import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';

/**
 * Min/max of one channel, left on the GPU.
 *
 * Returns a storage buffer whose first two floats are the minimum and maximum.
 * `matte_normalize.wgsl` consumes it directly, so RMBG's min/max normalisation
 * costs no readback and no pipeline stall.
 *
 * The caller owns the returned buffer and should hand it back with
 * `ctx.releaseBuffer`.
 */
export function minMax(ctx: GpuContext, src: GpuImage, channel = 0): GPUBuffer {
  const groupsX = workgroupCount(src.width, 8);
  const groupsY = workgroupCount(src.height, 8);
  const partialCount = groupsX * groupsY;

  const buffer = ctx.storageBuffer(Math.max(partialCount, 1) * 2 * 4, 0, 'minmax-partials');
  const bindings = [
    { kind: 'texture' as const, format: src.format },
    { kind: 'buffer' as const, access: 'read-write' as const },
    { kind: 'uniform' as const },
  ];

  ctx.dispatch(
    { module: 'reduce_minmax.wgsl', entryPoint: 'reduce_tex', bindings },
    [src, buffer, ctx.uniform(new Struct(16).i(0, src.width, src.height).u(8, channel, partialCount).bytes)],
    [groupsX, groupsY],
    'minmax:partials',
  );

  // Second stage runs in a single workgroup and collapses the partials in place.
  ctx.dispatch(
    { module: 'reduce_minmax.wgsl', entryPoint: 'reduce_final', bindings },
    [src, buffer, ctx.uniform(new Struct(16).i(0, src.width, src.height).u(8, channel, partialCount).bytes)],
    [1, 1],
    'minmax:final',
  );

  return buffer;
}
